import React, { useState, useRef } from 'react';
import { Shape } from '../types/Shape';
import '../styles/components/Minimap.css';

interface MinimapProps {
  shapes: Shape[];
  canvasSize: { width: number; height: number };
  canvasBounds: { minX: number; minY: number; maxX: number; maxY: number };
  viewportSize: { width: number; height: number };
  stagePos: { x: number; y: number };
  stageScale: number;
  onViewportChange: (pos: { x: number; y: number }) => void;
}

const Minimap: React.FC<MinimapProps> = ({
  shapes,
  canvasSize,
  canvasBounds,
  viewportSize,
  stagePos,
  stageScale,
  onViewportChange
}) => {
  const minimapWidth = 200;
  const minimapHeight = 200;
  const [isDragging, setIsDragging] = useState(false);
  const svgRef = useRef<SVGSVGElement>(null);

  // 미니맵 스케일 계산 (캔버스 전체를 미니맵에 맞춤)
  const scale = Math.min(
    minimapWidth / canvasSize.width,
    minimapHeight / canvasSize.height
  );

  // 캔버스 좌표를 미니맵 좌표로 변환하는 함수
  const toMinimapX = (canvasX: number) => (canvasX - canvasBounds.minX) * scale;
  const toMinimapY = (canvasY: number) => (canvasY - canvasBounds.minY) * scale;

  // 뷰포트 사각형 계산 (현재 화면에 보이는 캔버스 영역)
  const viewportCanvasX = -stagePos.x / stageScale;
  const viewportCanvasY = -stagePos.y / stageScale;
  const viewportRect = {
    x: toMinimapX(viewportCanvasX),
    y: toMinimapY(viewportCanvasY),
    width: (viewportSize.width / stageScale) * scale,
    height: (viewportSize.height / stageScale) * scale
  };

  // 미니맵 클릭/드래그 핸들러
  const handleMinimapInteraction = (e: React.MouseEvent<SVGSVGElement>) => {
    const svg = svgRef.current;
    if (!svg) return;

    const rect = svg.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;

    // 미니맵 좌표를 캔버스 좌표로 변환 (bounds offset 고려)
    const canvasX = x / scale + canvasBounds.minX;
    const canvasY = y / scale + canvasBounds.minY;

    // 뷰포트 중앙을 클릭한 위치로 이동
    const newStagePos = {
      x: -(canvasX - viewportSize.width / stageScale / 2) * stageScale,
      y: -(canvasY - viewportSize.height / stageScale / 2) * stageScale
    };

    onViewportChange(newStagePos);
  };

  const handleMouseDown = (e: React.MouseEvent<SVGSVGElement>) => {
    setIsDragging(true);
    handleMinimapInteraction(e);
  };

  const handleMouseMove = (e: React.MouseEvent<SVGSVGElement>) => {
    if (isDragging) {
      handleMinimapInteraction(e);
    }
  };

  const handleMouseUp = () => {
    setIsDragging(false);
  };

  return (
    <div className="minimap-container">
      <svg
        ref={svgRef}
        width={minimapWidth}
        height={minimapHeight}
        onMouseDown={handleMouseDown}
        onMouseMove={handleMouseMove}
        onMouseUp={handleMouseUp}
        onMouseLeave={handleMouseUp}
        style={{ cursor: isDragging ? 'grabbing' : 'grab' }}
      >
        {/* 배경 */}
        <rect
          x={0}
          y={0}
          width={minimapWidth}
          height={minimapHeight}
          fill="#f8fafc"
        />

        {/* 캔버스 전체 영역 */}
        <rect
          x={0}
          y={0}
          width={canvasSize.width * scale}
          height={canvasSize.height * scale}
          stroke="#e2e8f0"
          strokeWidth={1}
          fill="white"
        />

        {/* 객체들 렌더링 */}
        {shapes.map(shape => {
          if (shape.type === 'rect') {
            return (
              <rect
                key={shape.id}
                x={toMinimapX(shape.x)}
                y={toMinimapY(shape.y)}
                width={(shape.width || 100) * scale}
                height={(shape.height || 100) * scale}
                fill="#93c5fd"
                stroke="#3b82f6"
                strokeWidth={0.5}
              />
            );
          } else if (shape.type === 'pdf') {
            return (
              <rect
                key={shape.id}
                x={toMinimapX(shape.x)}
                y={toMinimapY(shape.y)}
                width={(shape.width || 100) * scale}
                height={(shape.height || 100) * scale}
                fill="#fecaca"
                stroke="#ef4444"
                strokeWidth={1}
              />
            );
          } else if (shape.type === 'circle') {
            return (
              <circle
                key={shape.id}
                cx={toMinimapX(shape.x)}
                cy={toMinimapY(shape.y)}
                r={(shape.radius || 50) * scale}
                fill="#a78bfa"
                stroke="#7c3aed"
                strokeWidth={0.5}
              />
            );
          } else if (shape.type === 'arrow') {
            const points = shape.points || [];
            const pathData = points.length >= 2
              ? `M ${toMinimapX(points[0])} ${toMinimapY(points[1])} ` +
                points.slice(2).reduce((acc, p, i) => {
                  if (i % 2 === 0) {
                    return acc + `L ${toMinimapX(p)} ${toMinimapY(points[i + 3])} `;
                  }
                  return acc;
                }, '')
              : '';
            return pathData ? (
              <path
                key={shape.id}
                d={pathData}
                stroke="#f59e0b"
                strokeWidth={1}
                fill="none"
              />
            ) : null;
          } else if (shape.type === 'code') {
            return (
              <rect
                key={shape.id}
                x={toMinimapX(shape.x)}
                y={toMinimapY(shape.y)}
                width={(shape.width || 100) * scale}
                height={(shape.height || 100) * scale}
                fill="#1e1e1e"
                stroke="#3b82f6"
                strokeWidth={0.5}
              />
            );
          } else if (shape.type === 'md') {
            return (
              <rect
                key={shape.id}
                x={toMinimapX(shape.x)}
                y={toMinimapY(shape.y)}
                width={(shape.width || 100) * scale}
                height={(shape.height || 100) * scale}
                fill="#dcfce7"
                stroke="#22c55e"
                strokeWidth={0.5}
              />
            );
          } else if (shape.type === 'image') {
            return (
              <rect
                key={shape.id}
                x={toMinimapX(shape.x)}
                y={toMinimapY(shape.y)}
                width={(shape.width || 100) * scale}
                height={(shape.height || 100) * scale}
                fill="#c4b5fd"
                stroke="#8b5cf6"
                strokeWidth={0.5}
              />
            );
          }
          return null;
        })}

        {/* 현재 뷰포트 표시 */}
        <rect
          x={viewportRect.x}
          y={viewportRect.y}
          width={viewportRect.width}
          height={viewportRect.height}
          stroke="#3b82f6"
          strokeWidth={2}
          fill="rgba(59, 130, 246, 0.1)"
          strokeDasharray="5,5"
        />
      </svg>
      <div className="minimap-info">
        {`${Math.round(stageScale * 100)}% 확대`}
      </div>
    </div>
  );
};

export default Minimap;
